/**
 * Title + preview derivation for `ai_sessions` rows joined to `ai_messages`.
 * Shared by `useRecentChatSessions` (global) and `useProjectConversations`
 * (per-project). Same row shape, same fallback chain.
 */

export interface ChatSessionDerivationRow {
  id: string;
  /** Workspace pin; absent only for legacy rows. */
  brand_kit_id?: string | null;
  title: string | null;
  status: string;
  updated_at: string;
  ai_messages: Array<{ role: string; content: string | null; parts: unknown; created_at: string }>;
}

export const SESSION_MESSAGE_WINDOW = 8;

export const SESSION_MESSAGE_EMBED = { referencedTable: 'ai_messages' } as const;

export function withChronologicalMessages<T extends { ai_messages?: unknown[] | null }>(
  rows: readonly T[],
): T[] {
  return rows.map((row) =>
    Array.isArray(row.ai_messages) ? { ...row, ai_messages: [...row.ai_messages].reverse() } : row,
  );
}

export function stripMd(text: string): string {
  return text
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*|__|~~|\*|_|`/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function firstTextFromParts(parts: unknown): string | null {
  if (!Array.isArray(parts)) return null;
  for (const part of parts) {
    if (
      part &&
      typeof part === 'object' &&
      'type' in part &&
      part.type === 'text' &&
      'text' in part &&
      typeof part.text === 'string'
    ) {
      return part.text;
    }
  }
  return null;
}

/** Synthetic workflow-kickoff turns (role 'user', text "[SYSTEM] ...") are
 * internal plumbing, not user intent — never title off them. */
function isSyntheticSystemText(text: string): boolean {
  return /^\[SYSTEM\]/i.test(text.trim());
}

export function deriveTitle(row: ChatSessionDerivationRow): string {
  if (row.title && row.title.trim()) return row.title.trim();
  const firstUser = row.ai_messages?.find((m) => {
    if (m.role !== 'user') return false;
    const text = m.content || firstTextFromParts(m.parts) || '';
    return !isSyntheticSystemText(text);
  });
  if (firstUser) {
    const text = firstUser.content || firstTextFromParts(firstUser.parts) || '';
    const clean = stripMd(text);
    if (clean) {
      return clean.length > 60 ? `${clean.slice(0, 60).trim()}…` : clean;
    }
  }
  // NOT `.find((m) => m.role === 'assistant')` — a workflow's first assistant
  // turn is usually a tool call (`request_user_approval`, `advance_workflow_step`)
  // with no text part, so the literal first assistant message is almost always
  // empty here. Scan for the first assistant message that actually HAS text —
  // e.g. a later "Here's your LinkedIn post" summary — instead of giving up
  // after checking only the first one.
  const firstAssistantWithText = row.ai_messages?.find((m) => {
    if (m.role !== 'assistant') return false;
    const text = m.content || firstTextFromParts(m.parts) || '';
    return !!stripMd(text);
  });
  if (firstAssistantWithText) {
    const text =
      firstAssistantWithText.content || firstTextFromParts(firstAssistantWithText.parts) || '';
    const clean = stripMd(text);
    return clean.length > 60 ? `${clean.slice(0, 60).trim()}…` : clean;
  }
  return 'Untitled conversation';
}

export function derivePreview(row: ChatSessionDerivationRow): string {
  const messages = row.ai_messages ?? [];
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    const text = m.content || firstTextFromParts(m.parts);
    if (text) {
      const clean = stripMd(text);
      if (clean) return clean.length > 80 ? `${clean.slice(0, 80).trim()}…` : clean;
    }
  }
  return '';
}
